import { Injectable } from '@angular/core';
import { DEVICE_TYPE, BROWSERS, BROWSER_MOBILE, WEBVIEW_TYPES } from '@app/pages/verify-identity/shared/constants/constants';
import { UAParser } from 'ua-parser-js';


@Injectable({
  providedIn: 'root'
})
export class DeviceDetectionService {
  private parser: UAParser;

  constructor() {
    this.parser = new UAParser();
  }

  /**
   * Obtiene la información completa del dispositivo
   */
  getFullDeviceInfo() {
    return {
      browser: this.getBrowserInfo(),
      device: this.getDeviceInfo(),
      os: this.getOSInfo(),
      network: this.getNetworkInfo(),
      screen: this.getScreenInfo()
    };
  }

  /**
   * Obtiene información del navegador
   */
  getBrowserInfo() {
    const browser = this.parser.getBrowser();
    return {
      name: browser.name || 'Desconocido',
      version: browser.version || 'Desconocido',
      engine: this.parser.getEngine().name || 'Desconocido'
    };
  }

  /**
   * Obtiene información del dispositivo
   */
  getDeviceInfo() {
    const device = this.parser.getDevice();
    return {
      type: device.type || 'desktop',
      model: device.model || 'Desconocido',
      vendor: device.vendor || 'Desconocido',
      isMobile: this.parser.getDevice().type === 'mobile',
      isTablet: this.parser.getDevice().type === 'tablet'
    };
  }

  /**
   * Obtiene información del sistema operativo
   */
  getOSInfo() {
    const os = this.parser.getOS();
    return {
      name: os.name || 'Desconocido',
      version: os.version || 'Desconocido'
    };
  }

  /**
   * Obtiene información de la red
   */
  getNetworkInfo() {
    const connection = (navigator as any).connection;
    return {
      online: navigator.onLine,
      effectiveType: connection?.effectiveType || 'Desconocido',
      downlink: connection?.downlink || 'Desconocido',
      rtt: connection?.rtt || 'Desconocido'
    };
  }

  /**
   * Obtiene información de la pantalla
   */
  getScreenInfo() {
    return {
      width: window.screen.width,
      height: window.screen.height,
      orientation: window.screen.orientation.type,
      pixelRatio: window.devicePixelRatio,
      colorDepth: window.screen.colorDepth
    };
  }

  /**
   * Verifica si el dispositivo es iOS (iPhone, iPad, iPod)
   */
  isIOSDevice(): boolean {
    const os = this.getOSInfo();
    return os.name === DEVICE_TYPE.IOS;
  }

  /**
   * Verifica si el dispositivo es un iPhone específicamente
   */
  isIPhone(): boolean {
    const device = this.getDeviceInfo();
    return this.isIOSDevice() && device.model?.includes('iPhone');
  }

  /**
   * Verifica si el dispositivo es un iPad específicamente
   */
  isIPad(): boolean {
    const device = this.getDeviceInfo();
    const os = this.getOSInfo();
    
    // iPad detection is tricky in newer iPadOS versions
    return (
      this.isIOSDevice() && device.model?.includes('iPad') || 
      // iPadOS puede reportarse como macOS en iPads modernos con Safari
      (os.name?.includes('Mac') && navigator.maxTouchPoints > 1)
    );
  }

  /**
   * Detecta si está ejecutándose en un WebView de iOS
   * (Parte de una aplicación nativa iOS)
   */
  isIOSWebView(): boolean {
    const ua = navigator.userAgent;
    const os = this.getOSInfo();
    const browser = this.getBrowserInfo();
    
    // iOS con WebView generalmente
    if (os.name === DEVICE_TYPE.IOS) {
      // Patrones comunes de WebView iOS:
      // 1. No contiene "Safari/"
      // 2. Contiene marcadores específicos de frameworks (CriOS para Chrome, FxiOS para Firefox)
      // 3. Contiene identificadores de aplicaciones específicas
      
      const isSafari = browser.name === BROWSERS.SAFARI || browser.name === BROWSER_MOBILE.MOBILE_SAFARI;
      
      return (
        // Si no es Safari puede ser un WebView
        !isSafari ||
        // WebView generalmente no incluye la versión de Safari
        !ua.includes('Safari/') ||
        // Patrones específicos de WebView
        ua.includes('UIWebView') ||
        ua.includes('WKWebView') ||
        // Cordova/PhoneGap
        ua.includes('cordova') ||
        // Frameworks específicos
        ua.includes('FBAN/') || // Facebook app
        ua.includes('FBAV/') || // Facebook app
        ua.includes('Twitter') || // Twitter app
        ua.includes('Instagram')  // Instagram app
      );
    }
    
    return false;
  }

  /**
   * Detecta si está ejecutándose en cualquier tipo de WebView
   */
  isInAnyWebView(): boolean {
    return this.isIOSWebView() || this.isAndroidWebView();
  }

  /**
   * Detecta si está ejecutándose en un WebView de Android
   */
  isAndroidWebView(): boolean {
    const ua = navigator.userAgent;
    const os = this.getOSInfo();
    
    if (os.name === DEVICE_TYPE.ANDROID) {
      return (
        ua.includes('wv') ||
        ua.includes('WebView') ||
        (ua.includes('Android') && !ua.includes('Chrome/')) ||
        ua.includes('cordova')
      );
    }
    
    return false;
  }

  /**
   * Obtiene el tipo específico de entorno
   * Útil para aplicar lógica específica por plataforma
   */
  getEnvironmentType(): string {
    if (this.isIOSWebView()) {
      return WEBVIEW_TYPES.IOS_WEBVIEW;
    } else if (this.isAndroidWebView()) {
      return WEBVIEW_TYPES.ANDROID_WEBVIEW;
    } else if (this.isIOSDevice()) {
      return DEVICE_TYPE.IOS;
    } else if (this.getOSInfo().name === DEVICE_TYPE.ANDROID) {
      return DEVICE_TYPE.ANDROID;
    } else {
      return DEVICE_TYPE.WEB;
    }
  }
}